import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { apiFetch } from "../utils/apifetch";
import "./Act.css";

//const API = import.meta.env.VITE_API_URL;
const SEARCH_DEBOUNCE_MS = 300;

const emptyForm = {
  id: null,
  shortName: "",
  longName: "",
  activeStatus: "active",
};

export default function Act() {
  const navigate = useNavigate();
  const { logout, hasPermission } = useAuth();

  const [acts, setActs] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");

  /* ===== TOAST ===== */
  const [showToast, setShowToast] = useState(false);

  /* ===== SEARCH ===== */
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  /* ===== SORT ===== */
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  /* ===== FORM ===== */
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const isEdit = !!form.id;

  const authHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Content-Type": "application/json",
  });

  /* ===== DEBOUNCE ===== */
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(t);
  }, [searchInput]);

  /* ===== LOAD DATA ===== */
  const loadActs = async () => {
    try {
      const res = await apiFetch(
        `/act`,
        { headers: authHeader() },
        logout,
        navigate
      );
      setActs(await res.json());
    } catch (err) {
      setError(err.message || "Unable to load acts");
    }
  };

  const loadSummary = async () => {
    try {
      const res = await apiFetch(
        `/act/summary`,
        { headers: authHeader() },
        logout,
        navigate
      );
      if (res.ok) setSummary(await res.json());
    } catch {}
  };

  useEffect(() => {
    Promise.all([loadActs(), loadSummary()]).finally(() =>
      setLoading(false)
    );
  }, []);

  /* ===== SORT ===== */
  const handleSort = (key) => {
    setSortDir((prev) =>
      sortKey === key ? (prev === "asc" ? "desc" : "asc") : "asc"
    );
    setSortKey(key);
  };

  /* ===== FILTER + SORT ===== */
  const filteredActs = useMemo(() => {
    const q = search.toLowerCase();

    let data = acts.filter((a) =>
      a.shortName?.toLowerCase().includes(q) ||
      a.longName?.toLowerCase().includes(q) ||
      a.activeStatus?.toLowerCase().includes(q)
    );

    if (sortKey) {
      data = [...data].sort((a, b) => {
        const x = (a[sortKey] ?? "").toString().toLowerCase();
        const y = (b[sortKey] ?? "").toString().toLowerCase();
        if (x < y) return sortDir === "asc" ? -1 : 1;
        if (x > y) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    }

    return data;
  }, [acts, search, sortKey, sortDir]);

  /* ===== FORM HANDLERS ===== */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddAct = () => {
    setForm({ ...emptyForm });
    setFormError("");
    setShowForm(true);
  };

  const handleEditAct = (a) => {
    setForm({
      id: a.id,
      shortName: a.shortName,
      longName: a.longName,
      activeStatus: a.activeStatus,
    });
    setFormError("");
    setShowForm(true);
  };

  const resetForm = () => {
    setForm({ ...emptyForm });
    setFormError("");
    setShowForm(false);
  };

  const submit = async () => {
    const method = isEdit ? "PUT" : "POST";
    const url = isEdit ? `/act/${form.id}` : `/act`;

    const payload = {
      shortName: form.shortName,
      longName: form.longName,
      activeStatus: form.activeStatus,
      createdByUid: localStorage.getItem("uid"),
    };

    try {
      const res = await apiFetch(
        url,
        {
          method,
          headers: authHeader(),
          body: JSON.stringify(payload),
        },
        logout,
        navigate
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      resetForm();
      loadActs();
      loadSummary();

      /* ===== TOAST ONLY FOR ADD ===== */
      if (!isEdit) {
        setShowToast(true);
        setTimeout(() => setShowToast(false), 5000);
      }
    } catch (err) {
      setFormError(err.message || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this act?")) return;

    await apiFetch(
      `/act/${id}`,
      { method: "DELETE", headers: authHeader() },
      logout,
      navigate
    );

    loadActs();
    loadSummary();
  };

  /* ===== UI ===== */
  return (
    <div className="act-page">
      <h2>List of Acts</h2>

      <div className="act-cards">
        <div className="act-card total">
          Total Acts <span>{summary.total}</span>
        </div>
        <div className="act-card active">
          Active Acts <span>{summary.active}</span>
        </div>
        <div className="act-card inactive">
          Inactive Acts <span>{summary.inactive}</span>
        </div>
      </div>

      <div className="act-filters">
        <input
          placeholder="Search by Act Short Name & Act Full Name"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />

        <button
          className="add-act-btn"
          onClick={handleAddAct}
          disabled={!hasPermission("act", "create")}
        >
          + Add Act
        </button>
      </div>

      {showForm && (
        <div className="act-inline-form">
          <input
            name="shortName"
            placeholder="Act Short Name"
            value={form.shortName}
            onChange={handleChange}
          />
          <input
            name="longName"
            placeholder="Act Full Name"
            value={form.longName}
            onChange={handleChange}
          />
          <select
            name="activeStatus"
            value={form.activeStatus}
            onChange={handleChange}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <button className="save-btn" onClick={submit}>Save</button>
          <button className="cancel-btn" onClick={resetForm}>Cancel</button>
        </div>
      )}

      {!loading && (
        <table className="act-table">
          <thead>
            <tr>
              <th onClick={() => handleSort("shortName")}>
                Act Short Name <span className="sort-icons">▲▼</span>
              </th>
              <th onClick={() => handleSort("longName")}>
                Act Full Name <span className="sort-icons">▲▼</span>
              </th>
              <th onClick={() => handleSort("activeStatus")}>
                Status <span className="sort-icons">▲▼</span>
              </th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {filteredActs.map((a) => (
              <tr key={a.id}>
                <td>{a.shortName}</td>
                <td>{a.longName}</td>
                <td>
                  <span className={`status ${a.activeStatus}`}>
                    {a.activeStatus}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="edit-btn"
                      onClick={() => handleEditAct(a)}
                      disabled={!hasPermission("act", "update")}
                    >
                      Edit
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => del(a.id)}
                      disabled={!hasPermission("act", "delete")}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {!filteredActs.length && (
              <tr>
                <td colSpan="4" className="no-data">
                  No Acts found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {error && <p className="form-error">{error}</p>}
      {formError && <p className="form-error">{formError}</p>}

      {showToast && (
        <div className="toast-center-success">
          Act successfully added
        </div>
      )}
    </div>
  );
}


